import { createSignal, For, Show } from "solid-js";
import type { VerificationResult } from "../../enums/govern";
import { GOVERN_ROLE_KEY, VERIFICATION_RESULT } from "../../enums/govern";
import { GovernEditorActions } from "./GovernEditorActions";
import { isAssigned } from "./governStatus";
import type { GovernCheck, GovernRoles } from "./governTypes";

const ROLE_FIELDS: Array<{ key: keyof GovernRoles; label: string }> = [
  { key: GOVERN_ROLE_KEY.DELIVERY_OWNER, label: "Delivery owner" },
  { key: GOVERN_ROLE_KEY.REVIEWER, label: "Reviewer" },
  { key: GOVERN_ROLE_KEY.APPROVER, label: "Approver" },
  { key: GOVERN_ROLE_KEY.VERIFIER, label: "Verifier" },
  { key: GOVERN_ROLE_KEY.REVIEW_OWNER, label: "Review owner" },
];

export function GovernRolesEditor(props: {
  roles: GovernRoles;
  onSave: (roles: GovernRoles) => void;
  onCancel: () => void;
}) {
  const [roles, setRoles] = createSignal({ ...props.roles });

  return (
    <form
      class="govern-editor-form"
      onSubmit={(event) => {
        event.preventDefault();
        props.onSave(
          Object.fromEntries(
            Object.entries(roles()).map(([key, value]) => [key, value.trim()]),
          ) as GovernRoles,
        );
      }}
    >
      <p>
        Assign only the people, teams or roles responsible for this delivery.
        One value may appear in more than one role.
      </p>
      <For each={ROLE_FIELDS}>
        {(field) => (
          <label>
            <span>{field.label}</span>
            <input
              value={roles()[field.key]}
              placeholder="Not assigned"
              onInput={(event) =>
                setRoles((current) => ({
                  ...current,
                  [field.key]: event.currentTarget.value,
                }))
              }
            />
          </label>
        )}
      </For>
      <GovernEditorActions onCancel={props.onCancel} />
    </form>
  );
}

export function GovernCheckEditor(props: {
  check: GovernCheck;
  onSave: (check: GovernCheck) => void;
  onCancel: () => void;
}) {
  const [result, setResult] = createSignal<VerificationResult>(
    props.check.result,
  );
  const [note, setNote] = createSignal(props.check.evidenceNote);
  const [person, setPerson] = createSignal(props.check.verifiedBy);
  const [link, setLink] = createSignal(props.check.evidenceLink);
  const [source, setSource] = createSignal(props.check.verificationSource);
  const [error, setError] = createSignal<string | null>(null);
  const isNotApplicable = () => result() === VERIFICATION_RESULT.NOT_APPLICABLE;

  const save = (event: SubmitEvent) => {
    event.preventDefault();
    if (result() !== VERIFICATION_RESULT.NOT_CHECKED && !note().trim()) {
      setError(
        isNotApplicable()
          ? "Record why this criterion does not apply."
          : "Record evidence or a failure note for this result.",
      );
      return;
    }
    if (result() !== VERIFICATION_RESULT.NOT_CHECKED && !isAssigned(person())) {
      setError(
        isNotApplicable()
          ? "Record who confirmed this does not apply."
          : "Record who verified this result.",
      );
      return;
    }
    props.onSave({
      ...props.check,
      result: result(),
      evidenceNote:
        result() === VERIFICATION_RESULT.NOT_CHECKED ? "" : note().trim(),
      verifiedBy:
        result() === VERIFICATION_RESULT.NOT_CHECKED ? "" : person().trim(),
      evidenceLink:
        result() === VERIFICATION_RESULT.NOT_CHECKED ? "" : link().trim(),
      verificationSource:
        result() === VERIFICATION_RESULT.NOT_CHECKED ? "" : source().trim(),
    });
  };

  return (
    <form class="govern-editor-form" onSubmit={save}>
      <p class="govern-editor-criterion">{props.check.criterionText}</p>
      <Show when={error()}>{(message) => <p role="alert">{message()}</p>}</Show>
      <label>
        <span>Result</span>
        <select
          value={result()}
          onChange={(event) => {
            setResult(event.currentTarget.value as VerificationResult);
            setError(null);
          }}
        >
          <option value="not_checked">Not checked</option>
          <option value="pass">Pass</option>
          <option value="fail">Fail</option>
          <option value="not_applicable">Not applicable</option>
        </select>
      </label>
      <Show when={result() !== VERIFICATION_RESULT.NOT_CHECKED}>
        <label>
          <span>
            {isNotApplicable() ? "Reason" : "Evidence or failure note"}
          </span>
          <textarea
            rows={5}
            value={note()}
            onInput={(event) => {
              setNote(event.currentTarget.value);
              setError(null);
            }}
          />
        </label>
        <label>
          <span>{isNotApplicable() ? "Confirmed by" : "Verified by"}</span>
          <input
            value={person()}
            onInput={(event) => {
              setPerson(event.currentTarget.value);
              setError(null);
            }}
          />
        </label>
        <label>
          <span>Evidence link (optional)</span>
          <input
            type="url"
            value={link()}
            onInput={(event) => setLink(event.currentTarget.value)}
          />
        </label>
        <label>
          <span>Verification source or date note (optional)</span>
          <input
            value={source()}
            onInput={(event) => setSource(event.currentTarget.value)}
          />
        </label>
      </Show>
      <GovernEditorActions onCancel={props.onCancel} />
    </form>
  );
}
